import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a mini cart drawer.
 * Opens automatically when items are added to cart, slides up from the bottom.
 * Features auto-close after 5 seconds and option to open full cart.
 *
 * @extends {DialogComponent}
 */
class MiniCartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * Handles the cart add event.
   * Opens the mini cart drawer when an item is added to the cart.
   *
   * @param {CustomEvent} event - The cart add event.
   */
  #handleCartAdd = async (event) => {
    // Don't open if there was an error adding to cart
    if (event.detail?.data?.didError) {
      return;
    }
    if (event.detail?.data?.source !== 'product-form-component') {
      return;
    }
    if (!(event.target instanceof HTMLElement)) {
      return;
    }
    if (
      event.target.closest('.quick-add') ||
      event.target.closest('.quick-add-modal') ||
      event.target.closest('quick-add-component') ||
      event.target.closest('quick-add-dialog')
    ) {
      return;
    }

    // The variant ID is passed as sourceId in the event
    const variantId = event.detail?.sourceId;

    // Fetch the current cart data since the event doesn't include it
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (cart && variantId) {
        const addedItem = cart.items?.find((item) => String(item.variant_id) === String(variantId));
        if (addedItem) {
          this.#updateAddedItemDisplay(addedItem);
        }
        // Update subtotal with current cart total
        this.#updateSubtotal(cart.total_price);
      }
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    }

    this.open();
  };

  /**
   * Formats a price in cents to a currency string.
   *
   * @param {number} cents - The price in cents.
   * @returns {string} The formatted price string.
   */
  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return window.Shopify?.currency?.active
      ? `${window.Shopify.currency.active} ${amount}`
      : `$${amount}`;
  }

  /**
   * Updates the subtotal display with the current cart total.
   *
   * @param {number} totalPrice - The cart total price in cents.
   */
  #updateSubtotal(totalPrice) {
    const subtotalEl = this.querySelector('.mini-cart-summary__subtotal-amount');
    if (subtotalEl) {
      subtotalEl.textContent = this.#formatMoney(totalPrice);
    }
  }

  /**
   * Updates the mini cart display to show only the just-added item.
   *
   * @param {Object} item - The cart item that was just added.
   */
  #updateAddedItemDisplay(item) {
    const container = this.querySelector('.mini-cart-drawer__items');
    if (!container) return;

    // Build the item HTML
    const imageHtml = item.image
      ? `<a href="${item.url}" class="mini-cart-item__image-wrapper">
          <img
            src="${item.image.replace(/(\.[^.]+)$/, '_180x$1')}"
            alt="${item.title}"
            class="mini-cart-item__image"
            loading="lazy"
            width="80"
            height="100"
          >
        </a>`
      : '';

    const variantHtml = item.variant_title
      ? `<p class="mini-cart-item__variants">${item.variant_title}</p>`
      : '';

    container.innerHTML = `
      <div class="mini-cart-item">
        ${imageHtml}
        <div class="mini-cart-item__details">
          <a href="${item.url}" class="mini-cart-item__title">${item.product_title}</a>
          ${variantHtml}
          <div class="mini-cart-item__meta">
            <span class="mini-cart-item__qty">Qty: ${item.quantity}</span>
            <span class="mini-cart-item__price">${this.#formatMoney(item.final_line_price)}</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Opens the mini cart drawer.
   * Closes the full cart drawer if it's open (mutual exclusivity).
   */
  open() {
    // Close full cart drawer if it's open
    const fullCartDrawer = document.querySelector('cart-drawer-component');
    if (fullCartDrawer?.refs?.dialog?.open) {
      fullCartDrawer.closeDialog();
    }

    this.showDialog();
  }

  /**
   * Closes the mini cart drawer.
   */
  close() {
    this.closeDialog();
  }

  /**
   * Opens the full cart drawer and closes the mini cart.
   */
  openFullCart() {
    this.closeDialog();

    // Wait for close animation to complete, then open full cart
    setTimeout(() => {
      const fullCartDrawer = document.querySelector('cart-drawer-component');
      if (fullCartDrawer) {
        fullCartDrawer.open();
      }
    }, 350);
  }

}

if (!customElements.get('mini-cart-drawer-component')) {
  customElements.define('mini-cart-drawer-component', MiniCartDrawerComponent);
}
